import { ErrorMessage, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import React from 'react';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const START_YEAR = 1990;

function generateYearOptions(): number[] {
  const currentYear = DateTime.utc().year;
  const years: number[] = [];
  for (let year = currentYear; year >= START_YEAR; year--) {
    years.push(year);
  }
  return years;
}

function generateDayOptions(month: number | null, year: number | null): number[] {
  if (!month || !year) {
    // Default to 31 days if month/year not selected
    return Array.from({ length: 31 }, (_, i) => i + 1);
  }
  const daysInMonth = DateTime.fromObject({ year, month }).daysInMonth ?? 31;
  return Array.from({ length: daysInMonth }, (_, i) => i + 1);
}

export interface BirthdaySelectProps {
  monthFieldName: string;
  dayFieldName: string;
  yearFieldName: string;
  errorFieldName: string;
  label?: string;
}

interface BirthdayFormValues {
  birthMonth: number | null;
  birthDay: number | null;
  birthYear: number | null;
}

export default function BirthdaySelect({
  monthFieldName,
  dayFieldName,
  yearFieldName,
  errorFieldName,
  label = 'Birthday',
}: BirthdaySelectProps) {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<BirthdayFormValues>();

  const selectedMonth = values.birthMonth;
  const selectedDay = values.birthDay;
  const selectedYear = values.birthYear;

  const yearOptions = generateYearOptions();
  const dayOptions = generateDayOptions(selectedMonth, selectedYear);

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFieldValue(monthFieldName, value ? parseInt(value, 10) : null);
    setFieldTouched(monthFieldName, true, false);

    // If selected day is now out of range for the new month, reset it
    if (selectedDay && selectedYear) {
      const newDaysInMonth = DateTime.fromObject({
        year: selectedYear,
        month: parseInt(value, 10),
      }).daysInMonth;
      if (newDaysInMonth && selectedDay > newDaysInMonth) {
        setFieldValue(dayFieldName, null);
      }
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFieldValue(dayFieldName, value ? parseInt(value, 10) : null);
    setFieldTouched(dayFieldName, true, false);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFieldValue(yearFieldName, value ? parseInt(value, 10) : null);
    setFieldTouched(yearFieldName, true, false);

    // Handle leap year edge case: if Feb 29 is selected and new year is not a leap year
    if (selectedMonth === 2 && selectedDay === 29 && value) {
      const newYear = parseInt(value, 10);
      const daysInFeb = DateTime.fromObject({ year: newYear, month: 2 }).daysInMonth;
      if (daysInFeb && selectedDay > daysInFeb) {
        setFieldValue(dayFieldName, null);
      }
    }
  };

  return (
    <div className="form-group">
      <div className="form-field">
        <label className="form-field__label">{label}</label>
        <div className="birthday-select">
          <select
            className="birthday-select__field"
            value={selectedYear ?? ''}
            onChange={handleYearChange}
            aria-label="Birth year"
          >
            <option value="">Year</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            className="birthday-select__field"
            value={selectedMonth ?? ''}
            onChange={handleMonthChange}
            aria-label="Birth month"
          >
            <option value="">Month</option>
            {MONTHS.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            className="birthday-select__field birthday-select__field--day"
            value={selectedDay ?? ''}
            onChange={handleDayChange}
            aria-label="Birth day (optional)"
          >
            <option value="">Day</option>
            {dayOptions.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        </div>
        <div className="birthday-select__hint">It's okay to guess if you don't know. Day is optional.</div>
        <ErrorMessage name={errorFieldName} component="div" className="form-field__error" />
      </div>
    </div>
  );
}
